import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { useForm, Controller } from "react-hook-form";
import { AddVendorProp, ISelectList } from "../types/vendor.type";
import { VendorFormModel } from "../model/vendor.model";
import { createVendor, getVendorById, getVendorByMobileNo, updateVendor } from "../services/vendor.service";
import { HttpStatus } from "pages/Core";
import notistack from "pages/Core/Notistack";

const vendorCategories: ISelectList[] = [
    { value: 1, label: "Gold Vendor" },
    { value: 2, label: "Diamond Vendor" },
];

const AddVendor: React.FC<AddVendorProp> = ({
    isAddVendorOpen,
    onClose,
    reloadDataSource,
    selectedId = null
}) => {

    const {
        register,
        handleSubmit,
        control,
        reset,
        setFocus,
        watch,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm<VendorFormModel>({
        mode: "onChange",
        defaultValues: {
            mobile: "",
            name: "",
            email: "",
            gstNumber: "",
            panNumber: "",
            category: null,
            creditDays: 0,
            rate: 0,
            isPreferredVendor: false
        }
    });

    const isMobileManuallyChanged = useRef(false);
    const [editVendorId, setEditVendorId] = useState<number | null>(null);

    const mobileValue = watch("mobile");

    const onSubmit = async (data: VendorFormModel) => {
        const vendorId = selectedId || editVendorId;

        const payload = VendorFormModel.setVendorFromData({
            ...data,
            id: vendorId || 0,
            category: data.category?.label,
            creditDays: Number(data.creditDays),
            rate: Number(data.rate),
        });

        const response = vendorId
            ? await updateVendor(payload)
            : await createVendor(payload);

        if (response.status === HttpStatus.Success) {
            notistack.notifyFromApi(response);
            resetForm();
            onClose();
            reloadDataSource();
        } else {
            notistack.notifyFromApi(response);
        }
    };

    const getVendorDetails = async () => {
        try {
            const response = await getVendorById(selectedId);
            if (response.status === HttpStatus.Success) {
                const selectedCategory = vendorCategories.find(
                    (opt) => opt.label === response.data?.category
                );

                reset({
                    mobile: response.data?.mobile || "",
                    name: response.data?.name || "",
                    email: response.data?.email || "",
                    gstNumber: response.data?.gstNumber || "",
                    panNumber: response.data?.panNumber || "",
                    category: selectedCategory || null,
                    creditDays: response.data?.creditDays || 0,
                    rate: response.data?.rate || 0,
                    isPreferredVendor: response.data?.isPreferredVendor || false
                });

                setEditVendorId(response.data.id);
            } else {
                notistack.notifyFromApi(response);
            }
        } catch (error: any) {
            console.error(error);
            notistack.error(error);
        }
    };

    useEffect(() => {
        if (selectedId && isAddVendorOpen) {
            getVendorDetails();
        }
    }, [selectedId, isAddVendorOpen]);

    useEffect(() => {
        if (isAddVendorOpen) {
            setFocus("mobile");
        }
    }, [isAddVendorOpen]);

    useEffect(() => {
        const checkMobileExists = async () => {
            if (
                !selectedId &&
                !editVendorId &&
                isMobileManuallyChanged.current &&
                mobileValue &&
                mobileValue.length === 10
            ) {
                try {
                    const response = await getVendorByMobileNo(mobileValue);

                    if (response.status === HttpStatus.Success && response.data) {
                        const selectedCategory = vendorCategories.find(
                            (opt) => opt.label === response.data?.category
                        );

                        setEditVendorId(response.data.id);

                        reset({
                            mobile: response.data?.mobile || "",
                            name: response.data?.name || "",
                            email: response.data?.email || "",
                            gstNumber: response.data?.gstNumber || "",
                            panNumber: response.data?.panNumber || "",
                            category: selectedCategory || null,
                            creditDays: response.data?.creditDays || 0,
                            rate: response.data?.rate || 0,
                            isPreferredVendor: response.data?.isPreferredVendor || false
                        });
                    }
                } catch (error: any) {
                    console.error(error);
                    notistack.error(error);
                }
            }
        };

        checkMobileExists();
    }, [mobileValue, selectedId, editVendorId]);

    const resetForm = () => {
        reset({
            mobile: "",
            name: "",
            email: "",
            gstNumber: "",
            panNumber: "",
            category: null,
            creditDays: 0,
            rate: 0,
            isPreferredVendor: false
        });
        setEditVendorId(null);
        isMobileManuallyChanged.current = false;
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    return (
        <Modal
            show={isAddVendorOpen}
            size="lg"
            onHide={handleClose}
            backdrop="static"
            dialogClassName="modal-dialog-centered"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    {editVendorId || selectedId ? "Update Vendor" : "Add Vendor"}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <label>Mobile No<span className="text-danger">*</span></label>
                            <input
                                className={`form-control form-control-sm ${errors.mobile ? "is-invalid" : ""}`}
                                maxLength={10}
                                {...register("mobile", {
                                    required: "Mobile number is required",
                                    pattern: {
                                        value: /^[0-9]\d{9}$/,
                                        message: "Enter a valid mobile number",
                                    },
                                    validate: (value) => {
                                        if (/^(\d)\1{9}$/.test(value)) {
                                            return "Mobile number cannot have all same digits";
                                        }
                                        if (["0000000000", "0987654321"].includes(value)) {
                                            return "Mobile number is invalid or too generic";
                                        }
                                        return true;
                                    },
                                    onChange: () => {
                                        isMobileManuallyChanged.current = true;
                                    }
                                })}
                                placeholder="Enter mobile number"
                                disabled={!!selectedId || !!editVendorId}
                            />
                            {errors.mobile && <span className="text-danger" style={{ fontSize: "11px" }}>{(errors.mobile.message)}</span>}
                        </div>

                        <div className="col-md-4 mb-3">
                            <label>Vendor Name <span className="text-danger">*</span></label>
                            <input
                                className={`form-control form-control-sm ${errors.name ? "is-invalid" : ""}`}
                                {...register("name", {
                                    required: "Vendor name is required",
                                    minLength: {
                                        value: 2,
                                        message: "Vendor name must be at least 2 characters long"
                                    },
                                    pattern: {
                                        value: /^(?!\s)([A-Za-z]+(\s{1,}[A-Za-z]+)*)$/,
                                        message: "Only letters and spaces are allowed. No leading or trailing spaces."
                                    }
                                })}
                                placeholder="Enter vendor name"
                            />
                            {errors.name && <span className="text-danger" style={{ fontSize: "11px" }}>{(errors.name.message)}</span>}
                        </div>

                        <div className="col-md-4 mb-3">
                            <label>Email</label>
                            <input
                                className="form-control form-control-sm"
                                type="email"
                                {...register("email", {
                                    pattern: {
                                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-z]{2,4}$/,
                                        message: "Invalid email address",
                                    },
                                })}
                                placeholder="Enter email"
                            />
                            {errors.email && <span className="text-danger" style={{ fontSize: "11px" }}>{(errors.email.message)}</span>}
                        </div>

                        <div className="col-md-4 mb-3">
                            <label>GST Number</label>
                            <input
                                className="form-control form-control-sm"
                                maxLength={15}
                                {...register("gstNumber",
                                    {
                                        pattern: {
                                            value:
                                                /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                                            message: "Invalid GST number format",
                                        },
                                    }
                                )}
                                onChange={(e) => {
                                    e.target.value = e.target.value.toUpperCase().trim();
                                }}
                                placeholder="Enter GST number"
                            />
                            {errors.gstNumber && <span className="text-danger" style={{ fontSize: "11px" }}>{(errors.gstNumber.message)}</span>}
                        </div>

                        <div className="col-md-4 mb-3">
                            <label>PAN Number</label>
                            <input
                                className="form-control form-control-sm"
                                maxLength={10}
                                {...register("panNumber", {
                                    pattern: {
                                        value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                                        message: "Invalid PAN format (e.g., ABCDE1234F)",
                                    },
                                })}
                                onChange={(e) => {
                                    e.target.value = e.target.value.toUpperCase().trim();
                                }}
                                placeholder="Enter pan number"
                            />
                            {errors.panNumber && <span className="text-danger" style={{ fontSize: "11px" }}>{(errors.panNumber.message)}</span>}
                        </div>

                        <div className="col-md-4 mb-3">
                            <label>Vendor Category <span className="text-danger">*</span></label>
                            <Controller
                                name="category"
                                rules={{ required: "Vendor category is required" }}
                                control={control}
                                render={({ field }) => {
                                    const selectedValues = field.value || [];
                                    return (<div className="position-relative" >
                                        <Select
                                            {...field}
                                            options={vendorCategories}
                                            closeMenuOnSelect={false}
                                            hideSelectedOptions={false}
                                            placeholder="--Select category--"
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            value={selectedValues}
                                            getOptionLabel={(e: ISelectList) => e.label}
                                            getOptionValue={(e: ISelectList) => e.value}
                                            styles={{
                                                control: (provided: any, state: any) => ({
                                                    ...provided,
                                                    minHeight: "27px",
                                                    height: "27px",
                                                    fontSize: "12px",
                                                    borderWidth: "1px",
                                                    borderRadius: "0.25rem",
                                                    padding: "0 2px",
                                                    borderColor: errors[field?.name] ? "#ff6c6c" : state.isFocused ? "#86b7fe" : "#ced4da",
                                                    "&:hover": {
                                                        borderColor: "#86b7fe",
                                                    },
                                                }),
                                                indicatorsContainer: (base: any) => ({
                                                    ...base,
                                                    height: "28px"
                                                }),
                                                dropdownIndicator: (base: any) => ({
                                                    ...base,
                                                    padding: "4px",
                                                    color: "#6c757d",
                                                    ":hover": {
                                                        color: "#495057"
                                                    }
                                                }),
                                                valueContainer: (base: any) => ({
                                                    ...base,
                                                    padding: "0 8px",
                                                }),
                                                input: (base: any) => ({
                                                    ...base,
                                                    margin: 0,
                                                    padding: 0,
                                                }),
                                            }}
                                            onChange={(selectedOption: ISelectList) => {
                                                field.onChange(selectedOption);
                                            }}
                                        />
                                    </div>);
                                }}
                            />
                            {errors.category && <span className="text-danger" style={{ fontSize: "11px" }}>{(errors.category.message as string)}</span>}
                        </div>

                        <div className="col-md-4 mb-3">
                            <label>Credit Days <span className="text-danger">*</span></label>
                            <input
                                type="number"
                                className={`form-control form-control-sm ${errors.creditDays ? "is-invalid" : ""}`}
                                placeholder="Enter credit days"
                                {...register("creditDays", {
                                    required: "Credit days are required",
                                    min: {
                                        value: 0,
                                        message: "Credit days cannot be negative",
                                    },
                                    validate: value =>
                                        Number.isInteger(Number(value)) || "Only whole numbers allowed",
                                })}
                            />
                            {errors.creditDays && (
                                <small className="text-danger">{errors.creditDays.message}</small>
                            )}
                        </div>

                        {/* <div className="col-md-4 mb-3">
                            <label>Rate <span className="text-danger">*</span></label>
                            <input
                                type="number"
                                step="0.01"
                                className={`form-control form-control-sm ${errors.rate ? "is-invalid" : ""}`}
                                placeholder="Enter rate"
                                {...register("rate", {
                                    required: "Rate is required",
                                    min: {
                                        value: 0,
                                        message: "Rate must be positive",
                                    },
                                    pattern: {
                                        value: /^\d+(\.\d{1,2})?$/,
                                        message: "Only up to 2 decimal places allowed",
                                    },
                                })}
                            />
                            {errors.rate && (
                                <small className="text-danger">{errors.rate.message}</small>
                            )}
                        </div> */}

                        <div className="col-md-12 mb-3 d-flex align-items-center mt-2">
                            <input
                                type="checkbox"
                                {...register("isPreferredVendor")}
                                className="form-check-input me-2"
                                id="preferredVendor"
                            />
                            <label htmlFor="preferredVendor">Preferred Vendor</label>
                        </div>

                        <div className="d-flex justify-content-end mt-3">
                            <button
                                type="button"
                                className="btn add-btn me-2"
                                onClick={handleClose}
                            >
                                Cancel
                            </button>
                            <button type="submit" className="btn submit-btn" disabled={isSubmitting}>
                                {editVendorId || selectedId ? "Update Vendor" : "Add Vendor"}
                            </button>
                        </div>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
};

export default AddVendor;